import { Globe, ShieldCheck } from 'lucide-react';
import {
  commercialCertificateStatusLabel,
  commercialCertificateTiles,
  type CommercialCertificateFields,
} from '../../lib/commercialCertificate';
import { BRAND_AMBER } from '../../lib/brandMark';

const GOLD_LOGO_SRC = '/favicon.png';

function verifyUrlFromFields(fields: CommercialCertificateFields): string {
  if (fields.verifyUrl) return fields.verifyUrl;
  const encoded = fields.qrSrc.match(/(?:^|[?&])data=([^&]+)/)?.[1];
  return encoded ? decodeURIComponent(encoded) : 'https://lyrixsa.ai/license/verify';
}

function CertificateBrand({ logoSrc }: { logoSrc: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8.4, width: '33.333%' }}>
      <img
        src={logoSrc}
        alt=""
        aria-hidden="true"
        width="32.4"
        height="32.4"
        style={{
          width: 32.4,
          height: 32.4,
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />
      <div>
        <p
          style={{
            margin: 0,
            color: '#FFFFFF',
            fontFamily: 'Space Grotesk',
            fontSize: 16.8,
            fontWeight: 600,
            lineHeight: 1.17,
            letterSpacing: '0.01em',
            textTransform: 'uppercase',
          }}
        >
          LYRIXSA <span style={{ color: BRAND_AMBER }}>AI</span>
        </p>
        <p
          style={{
            margin: '2.6px 0 0',
            color: '#D3D5DA',
            fontSize: 6.3,
            fontWeight: 500,
            lineHeight: 1.48,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          AI MUSIC STUDIO
        </p>
      </div>
    </div>
  );
}

function CertificateTitle() {
  return (
    <div style={{ marginTop: 34, width: '62.334%' }}>
      <h1
        style={{
          margin: 0,
          color: '#FFFFFF',
          fontFamily: 'Space Grotesk',
          fontSize: 24.7,
          fontWeight: 700,
          lineHeight: 0.96,
          letterSpacing: '0.012em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        COMMERCIAL LICENSE
      </h1>
      <p
        style={{
          margin: '9px 0 0',
          color: BRAND_AMBER,
          fontFamily: 'Space Grotesk',
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        CERTIFICATE OF RIGHTS
      </p>
      <p
        style={{
          margin: '18px 0 0',
          color: '#EAE3D5',
          fontSize: 8.8,
          fontWeight: 400,
          lineHeight: 1.62,
          maxWidth: '94%',
        }}
      >
        This document certifies that you have the full commercial rights
        <br />
        to use, sell, distribute, and profit from music created with{' '}
        <span style={{ color: BRAND_AMBER }}>Lyrixsa AI</span>
      </p>
    </div>
  );
}

function CertificateSealStars() {
  const stars = [
    { left: '14%', top: '28%' },
    { left: '30%', top: '12%' },
    { left: '50%', top: '2%' },
    { left: '70%', top: '12%' },
    { left: '86%', top: '28%' },
  ];

  return (
    <div
      style={{
        position: 'relative',
        width: '78%',
        height: 20,
        marginBottom: 7,
      }}
    >
      {stars.map((star, index) => (
        <span
          key={index}
          style={{
            position: 'absolute',
            left: star.left,
            top: star.top,
            transform: 'translate(-50%, -50%)',
            fontSize: 8,
            lineHeight: 1,
            color: '#F6D37A',
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function CertificateSeal() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 14,
        right: 22,
        width: 104,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: 104,
          height: 104,
          borderRadius: '50%',
          border: `5px solid ${BRAND_AMBER}`,
          boxShadow: 'inset 0 0 0 1.25px #E0B453, 0 6px 20px rgba(0, 0, 0, 0.24)',
          background: 'radial-gradient(circle at 50% 34%, #181A21 0%, #0A0B0E 74%)',
          color: '#F6D37A',
          padding: '10px 8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <CertificateSealStars />
        <p
          style={{
            margin: 0,
            fontSize: 6.1,
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          Commercial
        </p>
        <p
          style={{
            margin: '4px 0 0',
            color: '#FFFFFF',
            fontFamily: 'Space Grotesk',
            fontSize: 13.2,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          License
        </p>
        <div style={{ width: 24, borderTop: `1px solid ${BRAND_AMBER}`, margin: '5px 0 6px' }} />
        <p
          style={{
            margin: 0,
            fontSize: 6.1,
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          Unlocked
        </p>
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: 52,
          height: 45,
          marginTop: -5,
          padding: '12px 0 24px',
          background: 'linear-gradient(180deg, #F2C760 0%, #C98D14 100%)',
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 84%, 0 100%)',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.16)',
          display: 'grid',
          placeItems: 'center',
        }}
      />
    </div>
  );
}

function CertificateProfileSymbol({ avatarSrc }: { avatarSrc?: string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #20242F 0%, #0E1015 74%)',
        display: 'grid',
        placeItems: 'center',
        boxShadow: 'inset 0 0 0 1px rgba(231, 181, 69, 0.06)',
        overflow: 'hidden',
        alignSelf: 'start',
        marginTop: 1,
      }}
    >
      {avatarSrc ? (
        <img
          src={avatarSrc}
          alt=""
          aria-hidden="true"
          data-certificate-profile-photo="true"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <svg width="33.6" height="33.6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="7.25" r="3.1" stroke={BRAND_AMBER} strokeWidth="1.45" />
          <path
            d="M6.2 17.25C6.2 14.7 8.65 12.95 12 12.95C15.35 12.95 17.8 14.7 17.8 17.25C17.8 18.45 16.88 19.35 15.72 19.35H8.28C7.12 19.35 6.2 18.45 6.2 17.25Z"
            stroke={BRAND_AMBER}
            strokeWidth="1.45"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

function CertificateCalendarIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4.25" y="5.75" width="15.5" height="14" rx="2.2" stroke={BRAND_AMBER} strokeWidth="1.6" />
      <path d="M8 4V7.2" stroke={BRAND_AMBER} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M16 4V7.2" stroke={BRAND_AMBER} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M4.25 9.4H19.75" stroke={BRAND_AMBER} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CertificateStatusIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="7.35" r="3" stroke={BRAND_AMBER} strokeWidth="1.6" />
      <path
        d="M6.1 17.05C6.1 14.6 8.58 12.95 12 12.95C15.42 12.95 17.9 14.6 17.9 17.05C17.9 18.2 17 19.1 15.88 19.1H8.12C7 19.1 6.1 18.2 6.1 17.05Z"
        stroke={BRAND_AMBER}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CertificateBodyFirstCard({ fields }: { fields: CommercialCertificateFields }) {
  const leftDetails = [
    { label: 'Licensee', value: fields.customerName || '—' },
    { label: 'Email', value: fields.email || '—' },
    { label: 'License Type', value: 'Commercial License' },
    { label: 'License ID', value: fields.licenseNumber || '—' },
  ];
  const rightDetails = [
    { label: 'Date Issued', value: fields.purchaseDate || '—', icon: <CertificateCalendarIcon /> },
    { label: 'Valid Until', value: 'Lifetime', icon: <CertificateCalendarIcon /> },
    {
      label: 'License Status',
      value: commercialCertificateStatusLabel(fields.status),
      icon: <CertificateStatusIcon />,
      badge: true,
    },
  ];

  return (
    <div
      style={{
        height: '18.75%',
        margin: '18px 26px 0',
        borderRadius: 8.4,
        border: '1px solid #D6D8DE',
        background: '#FFFFFF',
        boxShadow: '0 2px 10px rgba(11, 11, 13, 0.04)',
        display: 'grid',
        gridTemplateColumns: '1fr 1px 1fr',
        alignItems: 'stretch',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          padding: '16px 18px',
          display: 'grid',
          gridTemplateColumns: '56px minmax(0, 1fr)',
          alignItems: 'center',
          columnGap: 14,
        }}
      >
        <CertificateProfileSymbol avatarSrc={fields.avatarSrc} />
        <div style={{ minWidth: 0 }}>
          {leftDetails.map((detail, index) => (
            <div key={detail.label} style={{ marginTop: index === 0 ? 0 : 11 }}>
              <p
                style={{
                  margin: 0,
                  color: '#5B5E66',
                  fontSize: 6.6,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {detail.label}
              </p>
              <p
                style={{
                  margin: '2px 0 0',
                  color: detail.label === 'Licensee' ? '#111318' : '#5B5E66',
                  fontSize: 8.6,
                  fontWeight: detail.label === 'Licensee' ? 700 : 500,
                  lineHeight: 1.35,
                  overflowWrap: 'anywhere',
                }}
              >
                {detail.value}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div
        aria-hidden="true"
        style={{
          width: 1,
          background: '#E3E5EA',
          margin: '18px 0',
        }}
      />
      <div
        style={{
          padding: '16px 18px',
          display: 'grid',
          gridTemplateColumns: '1fr minmax(112px, 0.72fr)',
          columnGap: 12,
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: 0 }}>
          {rightDetails.map((detail, index) => (
            <div
              key={detail.label}
              style={{
                display: 'grid',
                gridTemplateColumns: '18px minmax(0, 1fr)',
                columnGap: 10,
                alignItems: 'start',
                marginTop: index === 0 ? 0 : 14,
              }}
            >
              <div aria-hidden="true" style={{ marginTop: 1 }}>
                {detail.icon}
              </div>
              <div>
                <p
                  style={{
                    margin: 0,
                    color: '#5B5E66',
                    fontSize: 6.6,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {detail.label}
                </p>
                {detail.badge ? (
                  <div
                    style={{
                      marginTop: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: 999,
                      padding: '2px 7px',
                      background: '#1DA34A',
                      color: '#FFFFFF',
                      fontSize: 6.5,
                      fontWeight: 700,
                      lineHeight: 1,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {detail.value}
                  </div>
                ) : (
                  <p
                    style={{
                      margin: '2px 0 0',
                      color: '#5B5E66',
                      fontSize: 8.6,
                      fontWeight: 500,
                      lineHeight: 1.35,
                    }}
                  >
                    {detail.value}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ minWidth: 0, alignSelf: 'end', paddingBottom: 3 }}>
          <p
            style={{
              margin: '0 0 8px',
              color: '#20242D',
              fontFamily: 'Brush Script MT, Snell Roundhand, cursive',
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            Lyrixsa Team
          </p>
          <div style={{ width: '100%', borderTop: '1px solid #D7D0C0', marginBottom: 8 }} />
          <p style={{ margin: 0, color: '#2B2E36', fontSize: 7.4, fontWeight: 500, lineHeight: 1.45 }}>
            Lyrixsa AI Team
          </p>
          <p style={{ margin: '4px 0 0', color: '#2B2E36', fontSize: 7.4, fontWeight: 500, lineHeight: 1.45 }}>
            Authorized Signatory
          </p>
        </div>
      </div>
    </div>
  );
}

function CertificateRightsIcon({ title }: { title: string }) {
  const svgProps = {
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
  } as const;
  const strokeProps = {
    stroke: BRAND_AMBER,
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (title) {
    case 'Sell & distribute':
      return (
        <svg {...svgProps}>
          <circle cx="8.25" cy="18.1" r="1.45" {...strokeProps} />
          <circle cx="16.2" cy="18.1" r="1.45" {...strokeProps} />
          <path d="M3.8 5.8H6.2L7.65 14.3H17.45L19.25 8.25H8.7" {...strokeProps} />
        </svg>
      );
    case 'Music platforms':
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="8" {...strokeProps} />
          <path d="M8.7 13.1a4.1 4.1 0 0 1 6.6 0" {...strokeProps} />
          <path d="M7.05 10.2a6.35 6.35 0 0 1 9.9 0" {...strokeProps} />
          <path d="M10.4 16a2.1 2.1 0 0 1 3.2 0" {...strokeProps} />
        </svg>
      );
    case 'YouTube & videos':
      return (
        <svg {...svgProps}>
          <rect x="4.4" y="6.9" width="15.2" height="10.2" rx="2.2" {...strokeProps} />
          <path d="M10.3 9.9L15.1 12L10.3 14.1V9.9Z" {...strokeProps} />
        </svg>
      );
    case 'Client projects':
      return (
        <svg {...svgProps}>
          <rect x="4.2" y="8.4" width="15.6" height="10.3" rx="2" {...strokeProps} />
          <path d="M9.1 8.4V6.85C9.1 5.95 9.83 5.2 10.75 5.2H13.25C14.17 5.2 14.9 5.95 14.9 6.85V8.4" {...strokeProps} />
          <path d="M4.2 12H19.8" {...strokeProps} />
        </svg>
      );
    case 'Monetization':
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="8" {...strokeProps} />
          <path d="M12 7.7V16.3" {...strokeProps} />
          <path d="M14.35 9.5C13.9 8.85 13.1 8.45 12.15 8.45C10.8 8.45 9.95 9.15 9.95 10.15C9.95 11.15 10.72 11.6 12.25 11.95C13.78 12.3 14.55 12.75 14.55 13.8C14.55 14.8 13.68 15.55 12.2 15.55C11.05 15.55 10.1 15.05 9.55 14.2" {...strokeProps} />
        </svg>
      );
    case 'Social media':
      return (
        <svg {...svgProps}>
          <circle cx="9" cy="9.2" r="2.3" {...strokeProps} />
          <circle cx="15.1" cy="9.2" r="2.3" {...strokeProps} />
          <path d="M5.55 16.65C5.55 14.85 7.18 13.6 9 13.6C10.82 13.6 12.45 14.85 12.45 16.65" {...strokeProps} />
          <path d="M11.55 16.65C11.55 14.85 13.18 13.6 15 13.6C16.82 13.6 18.45 14.85 18.45 16.65" {...strokeProps} />
        </svg>
      );
    case 'No copyright claims':
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="8" {...strokeProps} />
          <path d="M14.95 9.45C14.3 8.65 13.25 8.2 12 8.2C9.95 8.2 8.45 9.85 8.45 12C8.45 14.15 9.95 15.8 12 15.8C13.25 15.8 14.3 15.35 14.95 14.55" {...strokeProps} />
        </svg>
      );
    case 'Unlimited use':
      return (
        <span
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: BRAND_AMBER,
            fontSize: 27.2,
            fontWeight: 300,
            lineHeight: 1,
            transform: 'translateY(-1px)',
          }}
        >
          ∞
        </span>
      );
    case 'Global rights':
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="8" {...strokeProps} />
          <path d="M4.5 12H19.5" {...strokeProps} />
          <path d="M12 4.2C14.15 6.25 15.35 9.02 15.35 12C15.35 14.98 14.15 17.75 12 19.8" {...strokeProps} />
          <path d="M12 4.2C9.85 6.25 8.65 9.02 8.65 12C8.65 14.98 9.85 17.75 12 19.8" {...strokeProps} />
        </svg>
      );
    case 'Royalty-free':
    default:
      return (
        <svg {...svgProps}>
          <path d="M12 4.2L18.35 6.95V11.4C18.35 15.25 16.2 17.95 12 19.6C7.8 17.95 5.65 15.25 5.65 11.4V6.95L12 4.2Z" {...strokeProps} />
          <path d="M9.4 12.25L11.2 14.05L14.85 10.2" {...strokeProps} />
        </svg>
      );
  }
}

function CertificateBodyRightsCard() {
  const rights = commercialCertificateTiles().slice(0, 10);

  return (
    <div
      style={{
        height: '27.53%',
        margin: '7px 26px 0',
        borderRadius: 8.4,
        border: '1px solid #D6D8DE',
        background: '#FFFFFF',
        boxShadow: '0 2px 10px rgba(11, 11, 13, 0.04)',
        padding: '4.2px 14px 10px',
        overflow: 'visible',
      }}
    >
      <p
        style={{
          margin: 0,
          color: '#111318',
          fontFamily: 'Space Grotesk',
          fontSize: 10.6,
          fontWeight: 700,
          lineHeight: 1.1,
          textTransform: 'uppercase',
        }}
      >
        Your Commercial Rights
      </p>
      <p style={{ margin: '3px 0 0', color: '#2B2E36', fontSize: 7.25, lineHeight: 1.45 }}>
        With this license, you are granted the following rights for all music created using{' '}
        <span style={{ color: BRAND_AMBER, fontWeight: 600 }}>Lyrixsa AI</span>:
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: '2.5px 8px',
          marginTop: 10,
        }}
      >
        {rights.map((right) => (
          <div key={right.title} style={{ textAlign: 'center', minWidth: 0 }}>
            <div
              aria-hidden="true"
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                margin: '0 auto 6px',
                background: 'radial-gradient(circle at 35% 30%, #1A1E27 0%, #090A0D 78%)',
                display: 'grid',
                placeItems: 'center',
                boxShadow: 'inset 0 0 0 1px rgba(231, 181, 69, 0.08)',
              }}
            >
              <CertificateRightsIcon title={right.title} />
            </div>
            <p
              style={{
                margin: 0,
                color: '#101114',
                fontSize: 6.9,
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {right.title}
            </p>
            <p
              style={{
                margin: '3px 0 0',
                color: '#555961',
                fontSize: 5.75,
                lineHeight: 1.45,
              }}
            >
              {right.body}
            </p>
            <p
              aria-hidden="true"
              style={{
                margin: '1px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1EAF57',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1,
                transform: 'translateY(-0.5px)',
              }}
            >
              ✓
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CertificateBodyFinalCard({ termsHost, logoSrc }: { termsHost: string; logoSrc: string }) {
  const notes = [
    'You own the rights to the music you create with Lyrixsa AI.',
    'This license grants you commercial usage rights worldwide.',
    'You may not resell or redistribute Lyrixsa AI itself.',
    'You may not claim Lyrixsa AI music as your original composition without creating significant additional modifications.',
    'This license is non-transferable.',
    'Lyrixsa AI retains the right to update these terms at any time.',
  ];

  return (
    <div
      style={{
        height: '15.5%',
        margin: '7px 26px 6px',
        borderRadius: 8.4,
        border: '1px solid #D6D8DE',
        background: '#FFFFFF',
        boxShadow: '0 2px 10px rgba(11, 11, 13, 0.04)',
        display: 'grid',
        gridTemplateColumns: '1fr 1px 1fr',
        alignItems: 'stretch',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          padding: '4.5px 14px 10px',
          minWidth: 0,
        }}
      >
        <p
          style={{
            margin: 0,
            color: '#111318',
            fontFamily: 'Space Grotesk',
            fontSize: 8.9,
            fontWeight: 700,
            lineHeight: 1.1,
            textTransform: 'uppercase',
          }}
        >
          Important Notes
        </p>
        <ul
          style={{
            margin: '6px 0 0',
            paddingLeft: 12,
            color: '#2B2E36',
            fontSize: 5.9,
            lineHeight: 1.45,
          }}
        >
          {notes.map((note) => (
            <li key={note} style={{ marginTop: 0.5 }}>
              {note}
            </li>
          ))}
        </ul>
        <p style={{ margin: '5px 0 0', color: '#2B2E36', fontSize: 5.9, lineHeight: 1.35 }}>
          For full terms and conditions, visit:
        </p>
        <div
          style={{
            marginTop: 5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            borderRadius: 999,
            padding: '4px 10px',
            background: '#111318',
            color: '#F4F0E8',
            fontSize: 6.1,
            lineHeight: 1,
          }}
        >
          <Globe size={10} strokeWidth={1.8} />
          <span>{termsHost}</span>
        </div>
      </div>
      <div
        aria-hidden="true"
        style={{
          width: 1,
          background: '#E3E5EA',
          margin: '14px 0',
        }}
      />
      <div
        style={{
          padding: '4.5px 14px 22px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <p
          style={{
            margin: 0,
            color: '#111318',
            fontFamily: 'Space Grotesk',
            fontSize: 8.9,
            fontWeight: 700,
            lineHeight: 1.1,
            textTransform: 'uppercase',
          }}
        >
          License Certificate
        </p>
        <p style={{ margin: '3px 0 0', color: '#2B2E36', fontSize: 6.1, lineHeight: 1.5 }}>
          This certificate confirms that the licensee has purchased the Commercial License Toolkit
          from Lyrixsa AI and is granted the rights listed in this document.
        </p>
        <p style={{ margin: '4px 0 0', color: '#2B2E36', fontSize: 6.1, lineHeight: 1.5 }}>
          Thank you for being a part of the Lyrixsa AI community and creating amazing music.
        </p>
        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
          }}
        >
          <img
            src={logoSrc}
            alt=""
            aria-hidden="true"
            width="28"
            height="28"
            style={{
              width: 28,
              height: 28,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              minWidth: 0,
              height: 28,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <p
              style={{
                margin: 0,
                color: '#111318',
                fontFamily: 'Space Grotesk',
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1,
                textTransform: 'uppercase',
              }}
            >
              LYRIXSA <span style={{ color: BRAND_AMBER }}>AI</span>
            </p>
            <p
              style={{
                margin: 0,
                color: '#666A72',
                fontSize: 7.6,
                fontWeight: 500,
                lineHeight: 1.1,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              AI MUSIC STUDIO
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CertificateFooter({ qrSrc, verifyUrl }: { qrSrc: string; verifyUrl: string }) {
  const verifyPath = verifyUrl.replace(/^https?:\/\//, '');

  return (
    <div
      style={{
        height: '9.091%',
        minHeight: 0,
        marginTop: 'auto',
        background: '#0C0D10',
        display: 'grid',
        gridTemplateColumns: '58px calc(50% - 102px) 44px minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: 14,
        padding: '12px 16px',
      }}
    >
      <img
        src={qrSrc}
        alt="Verification QR code"
        style={{
          width: 48,
          height: 48,
          borderRadius: 4,
          background: '#FFFFFF',
          justifySelf: 'start',
          marginLeft: 10,
          display: 'block',
        }}
      />
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            color: BRAND_AMBER,
            fontFamily: 'Space Grotesk',
            fontSize: 8.8,
            fontWeight: 700,
            lineHeight: 1.15,
            textTransform: 'uppercase',
          }}
        >
          Verify this license
        </p>
        <p style={{ margin: '4px 0 0', color: '#E6DEC9', fontSize: 7.2, lineHeight: 1.4 }}>
          Scan the QR code or visit the link below
          <br />
          to verify the authenticity of this license
        </p>
        <p style={{ margin: '4px 0 0', color: BRAND_AMBER, fontSize: 7.2, fontWeight: 700, lineHeight: 1.35 }}>
          {verifyPath}
        </p>
      </div>
      <div
        aria-hidden="true"
        style={{
          display: 'grid',
          placeItems: 'center',
          justifySelf: 'start',
          transform: 'translateX(96%)',
        }}
      >
        <ShieldCheck size={42} color={BRAND_AMBER} strokeWidth={1.55} />
      </div>
      <div style={{ minWidth: 0, paddingRight: 10, transform: 'translateX(16%)' }}>
        <p style={{ margin: 0, color: '#E6DEC9', fontSize: 7.4, lineHeight: 1.45 }}>
          This is an official license document from Lyrixsa AI.
        </p>
        <p style={{ margin: '4px 0 0', color: '#E6DEC9', fontSize: 7.4, lineHeight: 1.45 }}>
          Keep this certificate for your records.
        </p>
      </div>
    </div>
  );
}

export interface CertificateDocumentProps {
  fields: CommercialCertificateFields;
  scale?: number;
}

export function certificateVerifyUrl(fields: CommercialCertificateFields): string {
  return verifyUrlFromFields(fields);
}

export function certificateTermsHost(fields: CommercialCertificateFields): string {
  const verifyUrl = certificateVerifyUrl(fields);
  return `${verifyUrl.replace(/^https?:\/\//, '').split('/')[0]}/terms`;
}

export default function CertificateDocument({ fields, scale = 1 }: CertificateDocumentProps) {
  const verifyUrl = verifyUrlFromFields(fields);
  const termsHost = `${verifyUrl.replace(/^https?:\/\//, '').split('/')[0]}/terms`;
  const logoSrc = fields.logoSrc ?? GOLD_LOGO_SRC;

  return (
    <div
      aria-label="Commercial license certificate preview"
      style={{
        width: `${scale * 100}%`,
        maxWidth: 'none',
        aspectRatio: '1654 / 2339',
        background: '#FFFFFF',
        transformOrigin: 'top center',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '25%',
          minHeight: 0,
          overflow: 'hidden',
          background:
            'radial-gradient(circle at 84% 18%, rgba(231, 181, 69, 0.18), transparent 18%), radial-gradient(circle at 72% 56%, rgba(201, 162, 75, 0.11), transparent 26%), linear-gradient(135deg, #06070A 0%, #0F1014 54%, #171920 100%)',
          borderBottom: `2px solid ${BRAND_AMBER}`,
          padding: '28px 26px 24px',
        }}
      >
        <CertificateBrand logoSrc={logoSrc} />
        <CertificateTitle />
        <CertificateSeal />
      </div>
      <CertificateBodyFirstCard fields={fields} />
      <CertificateBodyRightsCard />
      <CertificateBodyFinalCard termsHost={termsHost} logoSrc={logoSrc} />
      <CertificateFooter qrSrc={fields.qrSrc} verifyUrl={verifyUrl} />
    </div>
  );
}
